import { useCallback, useEffect, useRef, useState } from 'react';
import axios, { isCancel } from '../../utils/axios';
import type {
  ChildPackageCandidate,
  ImportChildPackageRequest,
  MultiPackagePrintRequest,
  MultiPackagePrintResult,
} from '../../types/multiPackagePrint';

export type ChildPackageDiscoveryParams = {
  organizationId: number;
  sourcePackageId: number;
  facilityId: number;
};

export type MultiPackagePrintCapabilities = {
  enabled: boolean;
  relationshipContractVersion: number;
  nodeOperationContractVersion: number;
  requiredRelationshipContractVersion: number;
  requiredNodeOperationContractVersion: number;
  safetyLimits?: { maxSelectedChildren: number; maxRequestedLabels: number; maxEstimatedZipBytes: number };
};

export async function getMultiPackagePrintCapabilities(): Promise<MultiPackagePrintCapabilities> {
  const response = await axios.get<MultiPackagePrintCapabilities>('/multi-package-print/capabilities');
  return response.data;
}

export function useMultiPackagePrintCapabilities(enabled: boolean) {
  const [data, setData] = useState<MultiPackagePrintCapabilities | null>(null);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    let active = true;
    if (!enabled) {
      setData(null);
      setError(null);
      return () => { active = false; };
    }
    getMultiPackagePrintCapabilities().then((capabilities) => {
      if (active) setData(capabilities);
    }).catch((failure) => {
      if (active) {
        setData(null);
        setError(failure);
      }
    });
    return () => { active = false; };
  }, [enabled]);
  return { data, error, ready: data?.enabled === true
    && data.relationshipContractVersion >= data.requiredRelationshipContractVersion
    && data.nodeOperationContractVersion >= data.requiredNodeOperationContractVersion };
}

export async function getChildPackageCandidates(
  params: ChildPackageDiscoveryParams,
  signal?: AbortSignal,
): Promise<ChildPackageCandidate[]> {
  const response = await axios.get('/multi-package-print/children', { params, signal });
  return Array.isArray(response.data) ? response.data : [];
}

export async function generateMultiPackagePrint(request: MultiPackagePrintRequest): Promise<MultiPackagePrintResult> {
  const response = await axios.post('/multi-package-print/generate', request);
  return response.data;
}

export async function importChildPackage(request: ImportChildPackageRequest): Promise<ChildPackageCandidate> {
  const response = await axios.post('/multi-package-print/children/import', request);
  return response.data;
}

export async function retryMultiPackagePrint(
  organizationId: number, batchId: string,
): Promise<MultiPackagePrintResult> {
  const response = await axios.post(`/multi-package-print/retry/${encodeURIComponent(batchId)}`, null, {
    params: { organizationId },
  });
  return response.data;
}

export async function confirmMultiPackageOutput(
  organizationId: number, batchId: string, sequences: number[],
): Promise<MultiPackagePrintResult> {
  const response = await axios.post(`/multi-package-print/batch/${encodeURIComponent(batchId)}/output-confirmed`,
    sequences, { params: { organizationId } });
  return response.data;
}

export async function getMultiPackagePrint(
  organizationId: number, batchId: string,
): Promise<MultiPackagePrintResult> {
  const response = await axios.get(`/multi-package-print/batch/${encodeURIComponent(batchId)}`, {
    params: { organizationId },
  });
  return response.data;
}

export async function getMultiPackageRecoveryHistory(
  organizationId: number, packageId: number,
): Promise<MultiPackagePrintResult['items']> {
  const response = await axios.get('/multi-package-print/history', {
    params: { organizationId, packageId },
  });
  return Array.isArray(response.data) ? response.data : [];
}

export async function getIncompleteMultiPackageRecoveryHistory(
  organizationId: number,
): Promise<MultiPackagePrintResult[]> {
  const response = await axios.get('/multi-package-print/history/incomplete', {
    params: { organizationId },
  });
  return Array.isArray(response.data) ? response.data : [];
}

export function useChildPackageCandidates(
  params: ChildPackageDiscoveryParams | null,
  enabled: boolean,
) {
  const [data, setData] = useState<ChildPackageCandidate[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const requestIdRef = useRef(0);
  const identityRef = useRef('');
  const identity = params
    ? `${params.organizationId}:${params.sourcePackageId}:${params.facilityId}`
    : '';

  const refresh = useCallback(() => setRefreshVersion((version) => version + 1), []);
  const updateCandidate = useCallback((candidate: ChildPackageCandidate) => {
    setData((current) => {
      const index = current.findIndex((item) => item.metrcPackageId === candidate.metrcPackageId);
      if (index < 0) return [...current, candidate];
      return current.map((item, itemIndex) => itemIndex === index ? candidate : item);
    });
  }, []);

  useEffect(() => {
    const identityChanged = identityRef.current !== identity;
    identityRef.current = identity;
    if (!enabled || !params) {
      requestIdRef.current += 1;
      setData([]);
      setError(null);
      setIsLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    const requestId = ++requestIdRef.current;
    if (identityChanged) setData([]);
    setError(null);
    setIsLoading(true);
    getChildPackageCandidates(params, controller.signal)
      .then((candidates) => {
        if (requestId === requestIdRef.current) setData(candidates);
      })
      .catch((requestError) => {
        if (requestId === requestIdRef.current && !isCancel(requestError)) setError(requestError);
      })
      .finally(() => {
        if (requestId === requestIdRef.current && !controller.signal.aborted) setIsLoading(false);
      });

    return () => {
      requestIdRef.current += 1;
      controller.abort();
    };
  }, [enabled, identity, refreshVersion]);

  return { data, error, isLoading, refresh, updateCandidate };
}
